import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight } from "lucide-react";
import { EASE, hoursInfo, scrollToSection } from "./shared";

const HERO_IMG =
  "https://images.unsplash.com/photo-1783207938137-286e6df9fecf?q=85&w=2400&auto=format&fit=crop";

const LINES = ["HIS GRACE", "AUTO REPAIR"];

const SPEC_ROWS = [
  ["Technician", "Amos Olayemi"],
  ["Location", "580 Jersey Ave, Unit 2"],
  ["City", "New Brunswick, NJ 08901"],
  ["Phone", "732-339-2277"],
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.04, 1.16]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const { row, isOpen } = hoursInfo();

  return (
    <section
      ref={ref}
      id="top"
      className="relative flex min-h-screen flex-col justify-end overflow-hidden"
    >
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0"
        aria-hidden="true"
      >
        <img
          src={HERO_IMG}
          alt=""
          className="h-full w-full object-cover"
          loading="eager"
        />
      </motion.div>
      <div
        className="absolute inset-0 bg-gradient-to-r from-void via-void/70 to-void/40"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 bg-gradient-to-t from-void via-void/30 to-void/60"
        aria-hidden="true"
      />
      <div className="industrial-grid absolute inset-0 opacity-40" aria-hidden="true" />

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-[1500px] px-5 pb-14 pt-28 sm:px-8 lg:px-14"
      >
        <div className="grid grid-cols-1 items-end gap-12 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
              className="font-mono text-[11px] uppercase tracking-[0.32em] text-gold"
            >
              Auto Technician · New Brunswick, NJ 08901
            </motion.p>

            <h1
              data-testid="hero-headline-title"
              className="mt-5 font-display uppercase leading-[0.95] text-[clamp(2.75rem,6.5vw,7rem)] text-zinc-50"
            >
              {LINES.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-2">
                  <motion.span
                    className="block whitespace-nowrap"
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1, delay: 0.22 + i * 0.14, ease: EASE }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
              className="mt-6 text-lg text-zinc-300"
            >
              Customer satisfaction is our priority.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <a
                href="tel:+17323392277"
                data-testid="hero-call-to-book-button"
                className="flex items-center gap-2.5 rounded-lg bg-gold px-7 py-3.5 font-semibold text-zinc-950 transition-all duration-300 hover:bg-gold-hover hover:shadow-[0_0_28px_rgba(255,85,0,0.45)] active:scale-95"
              >
                <Phone size={16} />
                Call to book
              </a>
              <a
                href="#services"
                data-testid="hero-explore-services-button"
                onClick={(e) => scrollToSection(e, "#services")}
                className="group flex items-center gap-2.5 rounded-lg border border-zinc-700 bg-zinc-900/60 px-7 py-3.5 font-semibold text-zinc-100 backdrop-blur transition-all duration-300 hover:border-zinc-400 active:scale-95"
              >
                Explore services
                <ArrowRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </a>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: EASE }}
            className="flex lg:col-span-4 lg:justify-end"
          >
            <div
              data-testid="hero-spec-sheet"
              className="w-full max-w-sm rounded-xl border border-zinc-800 bg-zinc-950/80 p-6 backdrop-blur-md"
              style={{ boxShadow: "0 0 60px rgba(0,0,0,0.4)" }}
            >
              <div className="flex items-center justify-between gap-4">
                <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-zinc-200">
                  Shop Spec Sheet
                </p>
                <span
                  data-testid="hero-open-status"
                  className={`flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.16em] ${isOpen ? "text-emerald-400" : "text-zinc-300"}`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${isOpen ? "bg-emerald-400" : "bg-zinc-400"}`}
                  />
                  {isOpen ? "Open now" : "Closed"}
                </span>
              </div>
              <dl className="mt-4 divide-y divide-zinc-800/80">
                {SPEC_ROWS.map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between gap-4 py-2.5">
                    <dt className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-300">
                      {k}
                    </dt>
                    <dd className="text-right text-sm text-zinc-100">{v}</dd>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-4 py-2.5">
                  <dt className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-300">
                    Today
                  </dt>
                  <dd className="text-right text-sm text-zinc-100">{row.time}</dd>
                </div>
              </dl>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}
